import { Sun, Eye, ArrowRight, Wrench, Layers, Shield, Palette, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import BackgroundPattern from "@/components/BackgroundPattern";

const tools = [
  {
    icon: Sun,
    title: "Solar Savings Calculator",
    description: "See your estimated savings based on real NM solar data, current incentives, and local electricity rates.",
    href: "/tools/solar-calculator",
    cta: "Calculate Savings",
  },
  {
    icon: Layers,
    title: "Shingle Explorer",
    description: "Browse Owens Corning® shingle colors and styles on different house models.",
    href: "/tools/shingle-explorer",
    cta: "Explore Shingles",
  },
  {
    icon: Shield,
    title: "Total Protection System®",
    description: "Learn how the Owens Corning integrated roofing system protects your home.",
    href: "/tools/total-protection",
    cta: "Learn More",
  },
  {
    icon: Palette,
    title: "Design & Inspire",
    description: "Explore curated shingle and color pairings to find the perfect aesthetic.",
    href: "/tools/design-inspire",
    cta: "Get Inspired",
  },
];

const ToolsCTASection = () => (
  <section className="section-padding bg-primary text-primary-foreground relative overflow-hidden">
    <BackgroundPattern variant="tealprint" className="text-primary-foreground" />
    <div className="container mx-auto max-w-6xl relative z-10">
      {/* Featured Tool: Design EyeQ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="mb-16"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 mb-6">
            <Wrench className="h-8 w-8 text-primary-foreground" />
          </div>
          <p className="text-sm font-bold uppercase tracking-widest text-primary-foreground/60 mb-3">Exclusive Homeowner Tools</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4">
            Tools Most Contractors Don't Offer
          </h2>
          <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto leading-relaxed">
            We integrate premium roofing visualization tools directly into our website — empowering you to explore, compare, and decide before we ever pick up a hammer.
          </p>
        </div>

        {/* Design EyeQ Featured Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="relative bg-gradient-to-br from-primary-foreground/15 to-primary-foreground/5 border-2 border-primary-foreground/25 rounded-2xl p-8 md:p-10 max-w-3xl mx-auto"
        >
          <div className="absolute top-4 right-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-primary-foreground/20 text-primary-foreground rounded-full px-3 py-1.5">
              <Sparkles className="h-3 w-3" /> Most Popular
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center flex-shrink-0">
              <Eye className="h-10 w-10 md:h-12 md:w-12 text-primary-foreground" />
            </div>
            <div className="text-center md:text-left flex-1">
              <h3 className="text-2xl md:text-3xl font-black text-primary-foreground mb-3">Design EyeQ® Visualizer</h3>
              <p className="text-primary-foreground/80 text-lg leading-relaxed mb-5">
                Upload a photo of <strong className="text-primary-foreground">your actual home</strong> and virtually try on different Owens Corning shingle colors and styles. See exactly how your new roof will look before making a decision.
              </p>
              <Link to="/tools/design-eyeq">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-bold gap-2 h-14 px-8 text-base shadow-lg">
                  <Eye className="h-5 w-5" /> Try It On Your Home <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Other Tools Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {tools.map((tool, i) => (
          <motion.div
            key={tool.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
          >
            <Link to={tool.href} className="block bg-primary-foreground/5 border border-primary-foreground/15 rounded-xl p-6 text-center hover:bg-primary-foreground/10 transition-colors group h-full">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-foreground/10 mb-4 group-hover:bg-primary-foreground/20 transition-colors">
                <tool.icon className="h-6 w-6 text-primary-foreground" />
              </div>
              <h3 className="text-base font-bold text-primary-foreground mb-2">{tool.title}</h3>
              <p className="text-primary-foreground/70 text-sm leading-relaxed mb-4">{tool.description}</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-foreground group-hover:underline">
                {tool.cta} <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="text-center mt-10"
      >
        <Link to="/tools">
          <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-bold gap-2 h-14 px-8 text-base shadow-lg">
            View All Tools <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
      </motion.div>
    </div>
  </section>
);

export default ToolsCTASection;
